/**
 * TripBuilder — Full-featured plan editor for building custom trip itineraries.
 *
 * Route: /builder/:id
 *
 * Features:
 *  - Editable plan overview (title, location, dates, budget, currency, trip type)
 *  - Day-by-day itinerary management (add, remove, reorder days)
 *  - Typed plan items per day (flight, hotel, activity, meal, transport, custom)
 *  - Item time & location fields, type-specific metadata
 *  - Inline item editing with expand/collapse
 *  - Auto-save with debounce (saves 3s after last edit)
 *  - Unsaved changes warning on navigation
 *  - Cost breakdown by item type with visual indicators
 *  - Budget progress bar (over/under budget)
 *  - Plan status management: Draft → Locked (finalized)
 *  - Duplicate plan, Delete plan
 *  - Source trip reference when plan was cloned from a template
 *  - Responsive layout following the project's MUI theme
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Stack,
  TextField,
  Card,
  CardContent,
  CircularProgress,
  useTheme,
  alpha,
  Alert,
  Snackbar,
  IconButton,
  Collapse,
  Divider,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tooltip,
  LinearProgress,
  Menu,
  ListItemIcon,
  ListItemText,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  ArrowBack,
  Save,
  Add,
  Delete,
  Edit,
  Check,
  ExpandMore,
  ExpandLess,
  Flight,
  Hotel,
  DirectionsCar,
  Restaurant,
  Hiking,
  MoreHoriz,
  CalendarMonth,
  LocationOn,
  ContentCopy,
  Lock,
  LockOpen,
  KeyboardArrowUp,
  KeyboardArrowDown,
  AccessTime,
  MoreVert,
  CloudDone,
  Warning,
  Print,
  Link as LinkIcon,
  Send,
  Share,
  CheckCircle,
  Groups,
  Public,
  WorkspacePremium,
} from '@mui/icons-material';
import plansService from '../../services/plansService';
import authService from '../../services/authService';
import ButtonPill from '../ui/ButtonPill';
import BookPlanDialog from '../user/BookPlanDialog';

/* ─── Constants ─── */

const ITEM_TYPE_ICONS = {
  flight: Flight,
  hotel: Hotel,
  activity: Hiking,
  meal: Restaurant,
  transport: DirectionsCar,
  custom: MoreHoriz,
};

const ITEM_TYPE_LABELS = {
  flight: 'Flight',
  hotel: 'Hotel',
  activity: 'Activity',
  meal: 'Meal',
  transport: 'Transport',
  custom: 'Custom',
};

const ITEM_TYPE_COLORS = {
  flight: '#1976D2',
  hotel: '#7B1FA2',
  activity: '#2E7D32',
  meal: '#E65100',
  transport: '#0097A7',
  custom: '#616161',
};

const CURRENCY_OPTIONS = [
  { value: 'USD', label: 'USD ($)' },
  { value: 'EUR', label: 'EUR (\u20AC)' },
  { value: 'GBP', label: 'GBP (\u00A3)' },
  { value: 'KES', label: 'KES (KSh)' },
];

const TRIP_TYPE_OPTIONS = [
  { value: 'safari', label: 'Safari' },
  { value: 'beach', label: 'Beach' },
  { value: 'cultural', label: 'Cultural' },
  { value: 'adventure', label: 'Adventure' },
  { value: 'roadtrip', label: 'Road Trip' },
  { value: 'international', label: 'International' },
  { value: 'hiking', label: 'Hiking' },
  { value: 'camping', label: 'Camping' },
  { value: 'honeymoon', label: 'Honeymoon' },
  { value: 'corporate', label: 'Corporate' },
];

const AUTO_SAVE_DELAY = 3000; // 3 seconds debounce

/* ─── Helpers ─── */

const formatCurrency = (value, currency = 'USD') => {
  if (value == null || value === '') return '\u2014';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  return isNaN(num)
    ? '\u2014'
    : new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(num);
};

const getCurrencySymbol = (currency = 'USD') => {
  return { USD: '$', EUR: '\u20AC', GBP: '\u00A3', KES: 'KSh' }[currency] || '$';
};

/* ─── Date / duration helpers ─── */

const todayIso = () => new Date().toISOString().split('T')[0];

const isoAddDays = (iso, n) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  d.setDate(d.getDate() + n);
  return d.toISOString().split('T')[0];
};

// Inclusive day count: a trip from the 1st to the 3rd is 3 days (and 2 nights).
const daysInclusive = (startIso, endIso) => {
  if (!startIso || !endIso) return null;
  const s = new Date(startIso);
  const e = new Date(endIso);
  if (isNaN(s.getTime()) || isNaN(e.getTime())) return null;
  const diff = Math.round((e - s) / 86400000) + 1;
  return diff > 0 ? diff : null;
};

const durationLabel = (days) => {
  const n = parseInt(days, 10);
  if (!n || n < 1) return '';
  const nights = n - 1;
  const dayPart = `${n} day${n !== 1 ? 's' : ''}`;
  return nights > 0 ? `${dayPart}, ${nights} night${nights !== 1 ? 's' : ''}` : dayPart;
};

/* ─── Component ─── */

const TripBuilder = () => {
  const { id: planId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();

  // Core state
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // UI state
  const [expandedDays, setExpandedDays] = useState({});
  const [editingItem, setEditingItem] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, type: null, target: null });
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [statusAction, setStatusAction] = useState(null); // 'lock' | 'unlock'
  const [moreMenuAnchor, setMoreMenuAnchor] = useState(null);
  const [expandAll, setExpandAll] = useState(true);

  // Exit flow (Request this trip + Share)
  const [requestDialogOpen, setRequestDialogOpen] = useState(false);
  const [requestForm, setRequestForm] = useState({ num_travelers: 2, preferred_date: '', message: '' });
  const [requesting, setRequesting] = useState(false);
  const [requestDone, setRequestDone] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [myOpenRequest, setMyOpenRequest] = useState(null); // persists "Requested" state
  const [bookDialogOpen, setBookDialogOpen] = useState(false);
  const [publishing, setPublishing] = useState(false);

  // Refs
  const autoSaveTimer = useRef(null);
  const formRef = useRef(null);
  const isInitialLoad = useRef(true);

  // Form state
  const [form, setForm] = useState({
    title: '',
    description: '',
    location: '',
    duration: '',
    num_days: '',
    total_budget: '',
    currency: 'USD',
    trip_type: 'safari',
    start_date: '',
    end_date: '',
    plan_days: [],
    metadata: {},
    admin: {}, // admin superpowers: sell_price, category, difficulty, max_group_size, featured, internal_notes
  });

  /* ─── Data Loading ─── */

  const loadPlan = useCallback(async () => {
    if (!authService.isAuthenticated()) {
      navigate('/login', { state: { returnTo: `/builder/${planId}`, message: 'Your session ended. Sign in to get back to your trip plan.' } });
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const res = await plansService.getPlanById(planId);
      const planData = res?.plan ?? res?.data?.plan;
      if (!planData) {
        setError('Plan not found');
        return;
      }
      setPlan(planData);
      setMyOpenRequest(planData.my_open_request ?? null);
      const days = planData.plan_days ?? [];
      const newForm = {
        title: planData.title ?? '',
        description: planData.description ?? '',
        location: planData.location ?? '',
        duration: (() => {
          const d = daysInclusive(planData.start_date, planData.end_date);
          return d ? durationLabel(d) : (planData.duration ?? '');
        })(),
        num_days: (() => {
          const d = daysInclusive(planData.start_date, planData.end_date) ?? parseInt(planData.duration, 10);
          return d && d > 0 ? String(d) : '';
        })(),
        total_budget: planData.total_budget ?? '',
        currency: planData.currency ?? 'USD',
        trip_type: planData.trip_type ?? 'safari',
        start_date: planData.start_date ?? '',
        end_date: planData.end_date ?? '',
        metadata: planData.metadata ?? {},
        admin: (planData.metadata && planData.metadata.admin) ?? {},
        plan_days: days.map((d) => ({
          id: d.id,
          day: d.day ?? 0,
          date: d.date ?? '',
          title: d.title ?? '',
          notes: d.notes ?? '',
          plan_items: (d.plan_items ?? []).map((item) => ({
            id: item.id,
            item_type: item.item_type ?? 'activity',
            name: item.name ?? '',
            description: item.description ?? '',
            location: item.location ?? '',
            start_time: item.start_time ?? '',
            end_time: item.end_time ?? '',
            cost: item.cost ?? '',
            sort_order: item.sort_order ?? 0,
            metadata: item.metadata ?? {},
          })),
        })),
      };
      setForm(newForm);
      formRef.current = JSON.stringify(newForm);
      setExpandedDays(days.reduce((acc, d) => ({ ...acc, [d.id ?? d.day]: true }), {}));
      setHasUnsavedChanges(false);
      isInitialLoad.current = false;
    } catch (err) {
      if (err?.status === 401) {
        navigate('/login', { state: { returnTo: `/builder/${planId}`, message: 'Your session ended. Sign in to get back to your trip plan.' } });
      } else {
        setError(err?.message ?? 'Failed to load plan');
      }
    } finally {
      setLoading(false);
    }
  }, [planId, navigate]);

  useEffect(() => {
    loadPlan();
  }, [loadPlan]);

  /* ─── Unsaved Changes Warning ─── */

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  /* ─── Auto-save ─── */

  useEffect(() => {
    if (isInitialLoad.current || !plan || plan.status !== 'draft') return;

    const currentForm = JSON.stringify(form);
    if (currentForm === formRef.current) {
      setHasUnsavedChanges(false);
      return;
    }

    setHasUnsavedChanges(true);

    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(() => {
      handleSave(true);
    }, AUTO_SAVE_DELAY);

    return () => {
      if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  /* ─── Day / Item Helpers ─── */

  const toggleDay = (dayId) => {
    setExpandedDays((prev) => ({ ...prev, [dayId]: !prev[dayId] }));
  };

  const toggleAllDays = () => {
    const newState = !expandAll;
    setExpandAll(newState);
    const newExpanded = {};
    form.plan_days.forEach((day, i) => {
      newExpanded[day.id ?? `day-${i}`] = newState;
    });
    setExpandedDays(newExpanded);
  };

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const updateAdmin = (field, value) => {
    setForm((prev) => ({ ...prev, admin: { ...(prev.admin || {}), [field]: value } }));
  };

  const handlePublish = async (visibility) => {
    if (!plan) return;
    try {
      setPublishing(true);
      await handleSave(true); // persist the admin fields (metadata) first
      const res = await plansService.publishPlan(plan.id, visibility);
      setSnackbar({ open: true, message: res?.message ?? 'Done', severity: 'success' });
      await loadPlan(); // refresh the published state
    } catch (err) {
      setSnackbar({ open: true, message: err?.message ?? 'Publish failed', severity: 'error' });
    } finally {
      setPublishing(false);
    }
  };

  // Two-way sync between number of days, start date and end date.
  // Days + start -> end; start + end -> days; and keep the "X days, Y nights" label current.
  const handleDaysChange = (value) => {
    setForm((prev) => {
      if (value === '') return { ...prev, num_days: '', duration: '' };
      const days = Math.max(0, parseInt(value, 10) || 0);
      const next = { ...prev, num_days: String(days), duration: durationLabel(days) };
      if (days > 0 && prev.start_date) next.end_date = isoAddDays(prev.start_date, days - 1);
      return next;
    });
  };

  const handleStartDateChange = (value) => {
    setForm((prev) => {
      const next = { ...prev, start_date: value };
      const days = parseInt(prev.num_days, 10);
      if (value && days > 0) {
        next.end_date = isoAddDays(value, days - 1);
      } else if (value && prev.end_date) {
        const d = daysInclusive(value, prev.end_date);
        if (d) { next.num_days = String(d); next.duration = durationLabel(d); }
      }
      return next;
    });
  };

  const handleEndDateChange = (value) => {
    setForm((prev) => {
      const next = { ...prev, end_date: value };
      if (prev.start_date && value) {
        const d = daysInclusive(prev.start_date, value);
        if (d) { next.num_days = String(d); next.duration = durationLabel(d); }
      }
      return next;
    });
  };

  const updateDay = (dayIndex, field, value) => {
    setForm((prev) => {
      const days = [...prev.plan_days];
      days[dayIndex] = { ...days[dayIndex], [field]: value };
      return { ...prev, plan_days: days };
    });
  };

  const updateItem = (dayIndex, itemIndex, field, value) => {
    setForm((prev) => {
      const days = [...prev.plan_days];
      const items = [...(days[dayIndex].plan_items || [])];
      items[itemIndex] = { ...items[itemIndex], [field]: value };
      days[dayIndex] = { ...days[dayIndex], plan_items: items };
      return { ...prev, plan_days: days };
    });
  };

  const addDay = () => {
    const maxDay = form.plan_days.reduce((m, d) => Math.max(m, d.day ?? 0), 0);
    const newDayKey = `day-new-${Date.now()}`;
    setForm((prev) => ({
      ...prev,
      plan_days: [
        ...prev.plan_days,
        { day: maxDay + 1, title: '', notes: '', plan_items: [] },
      ],
    }));
    setExpandedDays((prev) => ({ ...prev, [newDayKey]: true }));
  };

  const removeDay = (dayIndex) => {
    setDeleteConfirm({ open: true, type: 'day', target: { dayIndex } });
  };

  const confirmRemoveDay = () => {
    const { dayIndex } = deleteConfirm.target;
    setForm((prev) => ({
      ...prev,
      plan_days: prev.plan_days.filter((_, i) => i !== dayIndex),
    }));
    setDeleteConfirm({ open: false, type: null, target: null });
  };

  const moveDayUp = (dayIndex) => {
    if (dayIndex === 0) return;
    setForm((prev) => {
      const days = [...prev.plan_days];
      [days[dayIndex - 1], days[dayIndex]] = [days[dayIndex], days[dayIndex - 1]];
      // Update day numbers
      return { ...prev, plan_days: days.map((d, i) => ({ ...d, day: i + 1 })) };
    });
  };

  const moveDayDown = (dayIndex) => {
    if (dayIndex >= form.plan_days.length - 1) return;
    setForm((prev) => {
      const days = [...prev.plan_days];
      [days[dayIndex], days[dayIndex + 1]] = [days[dayIndex + 1], days[dayIndex]];
      return { ...prev, plan_days: days.map((d, i) => ({ ...d, day: i + 1 })) };
    });
  };

  const addItem = (dayIndex, itemType = 'activity') => {
    const newItemIndex = form.plan_days[dayIndex]?.plan_items?.length ?? 0;
    setForm((prev) => {
      const days = [...prev.plan_days];
      const items = [...(days[dayIndex].plan_items || [])];
      items.push({
        item_type: itemType,
        name: '',
        description: '',
        location: '',
        start_time: '',
        end_time: '',
        cost: '',
        sort_order: items.length,
        metadata: {},
      });
      days[dayIndex] = { ...days[dayIndex], plan_items: items };
      return { ...prev, plan_days: days };
    });
    setEditingItem({ dayIndex, itemIndex: newItemIndex });
  };

  const removeItem = (dayIndex, itemIndex) => {
    setDeleteConfirm({ open: true, type: 'item', target: { dayIndex, itemIndex } });
  };

  const confirmRemoveItem = () => {
    const { dayIndex, itemIndex } = deleteConfirm.target;
    setForm((prev) => {
      const days = [...prev.plan_days];
      const items = (days[dayIndex].plan_items || []).filter((_, i) => i !== itemIndex);
      days[dayIndex] = { ...days[dayIndex], plan_items: items };
      return { ...prev, plan_days: days };
    });
    setEditingItem(null);
    setDeleteConfirm({ open: false, type: null, target: null });
  };

  const moveItemUp = (dayIndex, itemIndex) => {
    if (itemIndex === 0) return;
    setForm((prev) => {
      const days = [...prev.plan_days];
      const items = [...(days[dayIndex].plan_items || [])];
      [items[itemIndex - 1], items[itemIndex]] = [items[itemIndex], items[itemIndex - 1]];
      days[dayIndex] = {
        ...days[dayIndex],
        plan_items: items.map((it, i) => ({ ...it, sort_order: i })),
      };
      return { ...prev, plan_days: days };
    });
  };

  const moveItemDown = (dayIndex, itemIndex) => {
    const items = form.plan_days[dayIndex]?.plan_items || [];
    if (itemIndex >= items.length - 1) return;
    setForm((prev) => {
      const days = [...prev.plan_days];
      const its = [...(days[dayIndex].plan_items || [])];
      [its[itemIndex], its[itemIndex + 1]] = [its[itemIndex + 1], its[itemIndex]];
      days[dayIndex] = {
        ...days[dayIndex],
        plan_items: its.map((it, i) => ({ ...it, sort_order: i })),
      };
      return { ...prev, plan_days: days };
    });
  };

  /* ─── Save / Status Actions ─── */

  const buildPayload = useCallback(() => {
    // A plan needs a non-empty duration to finalize. Derive it from the days
    // field, else from the number of itinerary days, so it is never blank.
    const computedDuration =
      (form.duration && form.duration.trim()) ||
      durationLabel(form.num_days) ||
      (form.plan_days.length ? durationLabel(form.plan_days.length) : '');
    return {
      title: form.title,
      description: form.description,
      location: form.location,
      duration: computedDuration,
      total_budget: form.total_budget ? parseFloat(form.total_budget) : null,
      currency: form.currency,
      trip_type: form.trip_type,
      start_date: form.start_date || null,
      end_date: form.end_date || null,
      metadata: { ...(form.metadata || {}), admin: form.admin || {} },
      plan_days_attributes: form.plan_days.map((d, idx) => ({
        id: d.id || undefined,
        day: d.day ?? idx + 1,
        date: d.date || null,
        title: d.title || null,
        notes: d.notes || null,
        display_order: idx,
        plan_items_attributes: (d.plan_items || []).map((item, itemIdx) => ({
          id: item.id || undefined,
          item_type: item.item_type ?? 'activity',
          name: item.name || 'Untitled',
          description: item.description || null,
          location: item.location || null,
          start_time: item.start_time || null,
          end_time: item.end_time || null,
          cost: item.cost ? parseFloat(item.cost) : null,
          sort_order: item.sort_order ?? itemIdx,
          metadata: item.metadata || {},
        })),
      })),
    };
  }, [form]);

  const handleSave = useCallback(
    async (isAutoSave = false) => {
      if (!plan || saving) return;
      try {
        setSaving(true);
        const payload = buildPayload();
        const res = await plansService.updatePlan(plan.id, payload);
        const updated = res?.plan ?? res?.data?.plan;
        if (updated) {
          setPlan(updated);
          // Sync form with server response to pick up new IDs for newly created items
          const days = updated.plan_days ?? [];
          const syncedForm = {
            ...form,
            plan_days: days.map((d) => ({
              id: d.id,
              day: d.day ?? 0,
              date: d.date ?? '',
              title: d.title ?? '',
              notes: d.notes ?? '',
              plan_items: (d.plan_items ?? []).map((item) => ({
                id: item.id,
                item_type: item.item_type ?? 'activity',
                name: item.name ?? '',
                description: item.description ?? '',
                location: item.location ?? '',
                start_time: item.start_time ?? '',
                end_time: item.end_time ?? '',
                cost: item.cost ?? '',
                sort_order: item.sort_order ?? 0,
                metadata: item.metadata ?? {},
              })),
            })),
          };
          setForm(syncedForm);
          formRef.current = JSON.stringify(syncedForm);
        }
        setHasUnsavedChanges(false);
        setLastSavedAt(new Date());
        if (!isAutoSave) {
          setSnackbar({ open: true, message: 'Plan saved successfully', severity: 'success' });
        }
      } catch (err) {
        if (!isAutoSave) {
          setSnackbar({
            open: true,
            message: err?.message ?? 'Failed to save plan',
            severity: 'error',
          });
        }
      } finally {
        setSaving(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [plan, buildPayload]
  );

  const handleStatusChange = async () => {
    if (!plan) return;
    try {
      setSaving(true);
      let res;
      if (statusAction === 'lock') {
        // Save first, then lock
        await handleSave(true);
        res = await plansService.lockPlan(plan.id);
      } else {
        res = await plansService.unlockPlan(plan.id);
      }
      const updated = res?.plan;
      if (updated) {
        setPlan(updated);
        setSnackbar({
          open: true,
          message:
            statusAction === 'lock'
              ? 'Plan finalized successfully'
              : 'Plan reopened for editing',
          severity: 'success',
        });
      }
    } catch (err) {
      setSnackbar({
        open: true,
        message: err?.message ?? `Failed to ${statusAction} plan`,
        severity: 'error',
      });
    } finally {
      setSaving(false);
      setStatusDialogOpen(false);
      setStatusAction(null);
    }
  };

  const handleDuplicate = async () => {
    if (!plan) return;
    try {
      setSaving(true);
      const res = await plansService.duplicatePlan(plan.id);
      if (res?.redirect_url) {
        setSnackbar({ open: true, message: 'Plan duplicated! Redirecting...', severity: 'success' });
        setTimeout(() => navigate(res.redirect_url), 600);
      }
    } catch (err) {
      setSnackbar({
        open: true,
        message: err?.message ?? 'Failed to duplicate plan',
        severity: 'error',
      });
    } finally {
      setSaving(false);
      setMoreMenuAnchor(null);
    }
  };

  const handleDelete = async () => {
    if (!plan) return;
    try {
      await plansService.deletePlan(plan.id);
      setSnackbar({ open: true, message: 'Plan deleted', severity: 'success' });
      setTimeout(() => navigate('/user/plans'), 600);
    } catch (err) {
      setSnackbar({
        open: true,
        message: err?.message ?? 'Failed to delete plan',
        severity: 'error',
      });
    }
    setDeleteConfirm({ open: false, type: null, target: null });
  };

  const handlePrint = () => {
    setMoreMenuAnchor(null);
    window.print();
  };

  const handleRequestSubmit = async () => {
    if (!plan) return;
    try {
      setRequesting(true);
      // A request needs a saved, locked snapshot. If the user is still on a draft,
      // quietly save + lock this version first (no separate "finalize" step for them).
      if (plan.status === 'draft') {
        await handleSave(true);
        const lockRes = await plansService.lockPlan(plan.id);
        if (lockRes?.plan) setPlan(lockRes.plan);
      }
      const res = await plansService.requestPlan(plan.id, {
        num_travelers: parseInt(requestForm.num_travelers, 10) || 1,
        preferred_date: requestForm.preferred_date || null,
        message: requestForm.message || null,
      });
      setMyOpenRequest(res?.planRequest ?? { status: 'pending', created_at: new Date().toISOString() });
      setRequestDone(true);
      setSnackbar({
        open: true,
        message: 'Request sent. Our team will be in touch with a quote.',
        severity: 'success',
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: err?.message ?? 'Failed to submit your request',
        severity: 'error',
      });
    } finally {
      setRequesting(false);
    }
  };

  const handleShareToggle = async () => {
    if (!plan) return;
    try {
      setSharing(true);
      // Enabling a link on a draft: flush pending edits so the public page isn't stale.
      if (!isShared && plan.status === 'draft' && hasUnsavedChanges) {
        await handleSave(true);
      }
      const res = await plansService.sharePlan(plan.id);
      const updated = res?.plan;
      if (updated) {
        setPlan((prev) => ({ ...prev, is_shared: updated.is_shared, slug: updated.slug ?? prev.slug }));
        setSnackbar({
          open: true,
          message: updated.is_shared ? 'Share link is now live' : 'Sharing disabled',
          severity: 'success',
        });
      }
    } catch (err) {
      setSnackbar({
        open: true,
        message: err?.message ?? 'Failed to update sharing',
        severity: 'error',
      });
    } finally {
      setSharing(false);
    }
  };

  const handleCopyLink = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch {
      setSnackbar({ open: true, message: 'Could not copy the link', severity: 'error' });
    }
  };

  /* ─── Computed Values ─── */

  const calculatedTotal = useMemo(() => {
    return form.plan_days.reduce((sum, d) => {
      return sum + (d.plan_items || []).reduce((s, i) => s + (parseFloat(i.cost) || 0), 0);
    }, 0);
  }, [form.plan_days]);

  const costBreakdown = useMemo(() => {
    const breakdown = {};
    form.plan_days.forEach((d) => {
      (d.plan_items || []).forEach((item) => {
        const cost = parseFloat(item.cost) || 0;
        if (cost > 0) {
          breakdown[item.item_type] = (breakdown[item.item_type] || 0) + cost;
        }
      });
    });
    return breakdown;
  }, [form.plan_days]);

  const budgetPercentage = useMemo(() => {
    const budget = parseFloat(form.total_budget) || 0;
    if (budget <= 0) return 0;
    return Math.min((calculatedTotal / budget) * 100, 150);
  }, [calculatedTotal, form.total_budget]);

  const totalDays = form.plan_days.length;
  const totalItems = form.plan_days.reduce((s, d) => s + (d.plan_items?.length || 0), 0);
  const isDraft = plan?.status === 'draft';
  const isLocked = plan?.status === 'locked';
  const isShared = Boolean(plan?.is_shared);
  const shareUrl = plan?.slug ? `${window.location.origin}/p/${plan.slug}` : '';
  // The exit (Request / Share) is reachable from a draft too, as soon as the plan
  // has a title and at least one day. No scary "finalize" gate up front.
  const canRequestOrShare = Boolean(form.title?.trim()) && form.plan_days.length > 0;
  const showExit = isLocked || (isDraft && canRequestOrShare);

  // Admin superpowers: publish this plan as a marketplace trip.
  const isAdmin = authService.isAdmin();
  const published = plan?.published || null;
  const calculatedTotalNum = form.plan_days.reduce(
    (sum, d) => sum + (d.plan_items || []).reduce((s, i) => s + (parseFloat(i.cost) || 0), 0),
    0
  );
  const sellPriceNum = parseFloat(form.admin?.sell_price) || 0;
  const marginNum = sellPriceNum - calculatedTotalNum;

  /* ─── Render ─── */

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '70vh',
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error || !plan) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '70vh',
          p: 4,
        }}
      >
        <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
          {error || 'Plan not found'}
        </Typography>
        <ButtonPill
          variant="contained"
          startIcon={<ArrowBack />}
          onClick={() => navigate('/user/plans')}
        >
          Back to My Plans
        </ButtonPill>
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: theme.palette.background.default, minHeight: '100vh', pb: 6 }}>
      {/* ═══ Hero Header ═══ */}
      <Box
        sx={{
          position: 'relative',
          height: { xs: '28vh', md: '32vh' },
          overflow: 'hidden',
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            background: 'rgba(0,0,0,0.15)',
          },
        }}
      >
        <Container
          maxWidth="lg"
          sx={{
            position: 'relative',
            zIndex: 2,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-end',
            pb: 4,
          }}
        >
          {/* Top actions bar */}
          <Box
            sx={{
              position: 'absolute',
              top: 20,
              left: 24,
              right: 24,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <ButtonPill
              variant="contained"
              startIcon={<ArrowBack />}
              onClick={() => {
                if (hasUnsavedChanges) {
                  if (
                    window.confirm(
                      'You have unsaved changes. Are you sure you want to leave?'
                    )
                  ) {
                    navigate('/user/plans');
                  }
                } else {
                  navigate('/user/plans');
                }
              }}
              sx={{
                bgcolor: 'white',
                color: theme.palette.primary.main,
                '&:hover': { bgcolor: 'grey.100' },
              }}
            >
              Back
            </ButtonPill>

            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              {/* Auto-save indicator */}
              {hasUnsavedChanges && isDraft && (
                <Chip
                  icon={<CircularProgress size={14} />}
                  label="Saving..."
                  size="small"
                  sx={{ bgcolor: 'rgba(255,255,255,0.9)', fontWeight: 600 }}
                />
              )}
              {!hasUnsavedChanges && lastSavedAt && (
                <Chip
                  icon={<CloudDone sx={{ fontSize: 16 }} />}
                  label={`Saved ${lastSavedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`}
                  size="small"
                  sx={{ bgcolor: 'rgba(255,255,255,0.9)', color: theme.palette.success.main, fontWeight: 600 }}
                />
              )}

              {/* More menu */}
              <IconButton
                onClick={(e) => setMoreMenuAnchor(e.currentTarget)}
                sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.2)', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' } }}
              >
                <MoreVert />
              </IconButton>
              <Menu
                anchorEl={moreMenuAnchor}
                open={Boolean(moreMenuAnchor)}
                onClose={() => setMoreMenuAnchor(null)}
              >
                <MenuItem onClick={handleDuplicate}>
                  <ListItemIcon><ContentCopy fontSize="small" /></ListItemIcon>
                  <ListItemText>Duplicate Plan</ListItemText>
                </MenuItem>
                <MenuItem onClick={handlePrint}>
                  <ListItemIcon><Print fontSize="small" /></ListItemIcon>
                  <ListItemText>Print Plan</ListItemText>
                </MenuItem>
                {plan.source_trip_id && (
                  <MenuItem onClick={() => { setMoreMenuAnchor(null); navigate(`/trips/${plan.source_trip_id}`); }}>
                    <ListItemIcon><LinkIcon fontSize="small" /></ListItemIcon>
                    <ListItemText>View Source Trip</ListItemText>
                  </MenuItem>
                )}
                <Divider />
                <MenuItem
                  onClick={() => {
                    setMoreMenuAnchor(null);
                    setDeleteConfirm({ open: true, type: 'plan', target: null });
                  }}
                  sx={{ color: 'error.main' }}
                >
                  <ListItemIcon><Delete fontSize="small" color="error" /></ListItemIcon>
                  <ListItemText>Delete Plan</ListItemText>
                </MenuItem>
              </Menu>
            </Box>
          </Box>

          {/* Title & chips */}
          <Typography
            variant="h3"
            sx={{
              color: 'white',
              fontWeight: 700,
              fontFamily: '"Playfair Display", serif',
              textShadow: '2px 2px 8px rgba(0,0,0,0.3)',
              fontSize: { xs: '1.6rem', sm: '2rem', md: '2.6rem' },
            }}
          >
            {form.title || 'Untitled Plan'}
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', gap: 0.5 }}>
            {form.location && (
              <Chip
                icon={<LocationOn />}
                label={form.location}
                size="small"
                sx={{ bgcolor: 'rgba(255,255,255,0.9)', color: theme.palette.primary.dark }}
              />
            )}
            {form.duration && (
              <Chip
                icon={<CalendarMonth />}
                label={form.duration}
                size="small"
                sx={{ bgcolor: 'rgba(255,255,255,0.9)', color: theme.palette.primary.dark }}
              />
            )}
            <Chip
              label={isLocked ? 'Ready' : 'Draft'}
              size="small"
              sx={{
                bgcolor: isLocked ? alpha(theme.palette.success.main, 0.9) : 'rgba(255,255,255,0.9)',
                color: isLocked ? 'white' : theme.palette.primary.dark,
                fontWeight: 700,
              }}
            />
            {plan.source_trip_id && (
              <Chip
                label="Customized"
                size="small"
                sx={{ bgcolor: 'rgba(255,255,255,0.9)', color: theme.palette.info.main, fontWeight: 600 }}
              />
            )}
            <Chip
              label={`${totalDays} day${totalDays !== 1 ? 's' : ''} \u00B7 ${totalItems} item${totalItems !== 1 ? 's' : ''}`}
              size="small"
              sx={{ bgcolor: 'rgba(255,255,255,0.9)', color: theme.palette.text.secondary }}
            />
          </Stack>
        </Container>
      </Box>

      {/* ═══ Locked Banner ═══ */}
      {isLocked && (
        <Container maxWidth="lg" sx={{ mt: 2 }}>
          <Alert
            severity="info"
            icon={<Lock />}
            action={
              <ButtonPill
                size="small"
                variant="outlined"
                startIcon={<LockOpen />}
                onClick={() => { setStatusAction('unlock'); setStatusDialogOpen(true); }}
                sx={{ borderColor: 'currentColor', color: 'inherit' }}
              >
                Reopen to Edit
              </ButtonPill>
            }
            sx={{ borderRadius: 3 }}
          >
            This version is <strong>saved as ready</strong>. Reopen it anytime to make changes.
          </Alert>
        </Container>
      )}

      {/* ═══ The exit — Request the trip + Share (available from draft too) ═══ */}
      {showExit && (
        <Container maxWidth="lg" sx={{ mt: 3 }}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, md: 4 },
              borderRadius: 4,
              border: `2px solid ${alpha(theme.palette.success.main, 0.4)}`,
              background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.06)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
              <Box
                sx={{
                  width: 52,
                  height: 52,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: alpha(theme.palette.success.main, 0.15),
                  flexShrink: 0,
                }}
              >
                <CheckCircle sx={{ color: theme.palette.success.main, fontSize: 30 }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 220 }}>
                <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.text.primary }}>
                  {myOpenRequest?.approved ? 'Approved' : myOpenRequest ? 'Request sent' : 'Ready to send or share'}
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5, mb: 2.5 }}>
                  {myOpenRequest?.approved
                    ? 'Great news — our team approved this trip and will be in touch to finalize your booking. No payment is due yet.'
                    : myOpenRequest
                    ? 'Our team has your itinerary and will reach out with a custom quote. No payment is due yet.'
                    : 'Send this itinerary to our team for a free, no-obligation quote, or share a read-only link with your travel companions. You can keep editing anytime.'}
                </Typography>

                {myOpenRequest ? (
                  <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                    <Chip
                      icon={<CheckCircle sx={{ fontSize: 18, color: 'inherit !important' }} />}
                      label={myOpenRequest.approved ? 'Approved' : `Requested · ${myOpenRequest.status === 'contacted' ? 'In touch' : 'Pending'}`}
                      sx={{
                        bgcolor: alpha(theme.palette.success.main, 0.15),
                        color: theme.palette.success.dark,
                        fontWeight: 700,
                        px: 0.5,
                      }}
                    />
                    {myOpenRequest.created_at && (
                      <Typography variant="body2" color="text.secondary">
                        Sent {new Date(myOpenRequest.created_at).toLocaleDateString()}
                      </Typography>
                    )}
                    {myOpenRequest.approved && (
                      <ButtonPill
                        variant="contained"
                        color="primary"
                        size="small"
                        startIcon={<Send />}
                        onClick={() => setBookDialogOpen(true)}
                      >
                        Book this trip
                      </ButtonPill>
                    )}
                    <ButtonPill
                      variant="outlined"
                      color="primary"
                      size="small"
                      startIcon={<Share />}
                      onClick={() => setShareDialogOpen(true)}
                    >
                      {isShared ? 'Manage Share Link' : 'Share Plan'}
                    </ButtonPill>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                    <ButtonPill
                      variant="contained"
                      color="primary"
                      startIcon={<Send />}
                      onClick={() => { setRequestDone(false); setRequestDialogOpen(true); }}
                    >
                      Request This Trip
                    </ButtonPill>
                    <ButtonPill
                      variant="outlined"
                      color="primary"
                      startIcon={<Share />}
                      onClick={() => setShareDialogOpen(true)}
                    >
                      {isShared ? 'Manage Share Link' : 'Share Plan'}
                    </ButtonPill>
                  </Box>
                )}

                {isShared && shareUrl && (
                  <Box
                    sx={{
                      mt: 2.5,
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: theme.palette.background.paper,
                      border: `1px solid ${theme.palette.grey[200]}`,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      flexWrap: 'wrap',
                    }}
                  >
                    <Public sx={{ fontSize: 18, color: theme.palette.success.main }} />
                    <Typography
                      variant="body2"
                      sx={{ flex: 1, minWidth: 160, color: theme.palette.text.secondary, wordBreak: 'break-all' }}
                    >
                      {shareUrl}
                    </Typography>
                    <ButtonPill
                      variant="text"
                      size="small"
                      startIcon={<ContentCopy sx={{ fontSize: 16 }} />}
                      onClick={handleCopyLink}
                    >
                      {linkCopied ? 'Copied' : 'Copy'}
                    </ButtonPill>
                  </Box>
                )}
              </Box>
            </Box>
          </Paper>
        </Container>
      )}

      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* ═══ Plan Overview ═══ */}
        <Paper
          elevation={0}
          sx={{ p: { xs: 2.5, md: 4 }, mb: 4, borderRadius: 4, border: `2px solid ${theme.palette.grey[200]}` }}
        >
          <Typography
            variant="h5"
            sx={{ fontWeight: 700, mb: 3, color: theme.palette.primary.main }}
          >
            Plan Overview
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Title"
                value={form.title}
                onChange={(e) => updateField('title', e.target.value)}
                fullWidth
                variant="outlined"
                disabled={isLocked}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Location"
                value={form.location}
                onChange={(e) => updateField('location', e.target.value)}
                fullWidth
                variant="outlined"
                disabled={isLocked}
                placeholder="e.g. Masai Mara, Kenya"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label="Days"
                type="number"
                value={form.num_days}
                onChange={(e) => handleDaysChange(e.target.value)}
                fullWidth
                variant="outlined"
                disabled={isLocked}
                inputProps={{ min: 1 }}
                placeholder="e.g. 5"
                helperText={form.num_days ? durationLabel(form.num_days) : 'Nights fill in automatically'}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth disabled={isLocked}>
                <InputLabel>Trip Type</InputLabel>
                <Select
                  value={form.trip_type}
                  onChange={(e) => updateField('trip_type', e.target.value)}
                  label="Trip Type"
                >
                  {TRIP_TYPE_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label="Budget"
                type="number"
                value={form.total_budget}
                onChange={(e) => updateField('total_budget', e.target.value)}
                fullWidth
                variant="outlined"
                disabled={isLocked}
                InputProps={{
                  startAdornment: (
                    <Typography sx={{ mr: 0.5, color: theme.palette.text.secondary, fontWeight: 600 }}>
                      {getCurrencySymbol(form.currency)}
                    </Typography>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth disabled={isLocked}>
                <InputLabel>Currency</InputLabel>
                <Select
                  value={form.currency}
                  onChange={(e) => updateField('currency', e.target.value)}
                  label="Currency"
                >
                  {CURRENCY_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Start Date"
                type="date"
                value={form.start_date}
                onChange={(e) => handleStartDateChange(e.target.value)}
                fullWidth
                variant="outlined"
                disabled={isLocked}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: todayIso() }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="End Date"
                type="date"
                value={form.end_date}
                onChange={(e) => handleEndDateChange(e.target.value)}
                fullWidth
                variant="outlined"
                disabled={isLocked}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: form.start_date || todayIso() }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Description"
                value={form.description}
                onChange={(e) => updateField('description', e.target.value)}
                fullWidth
                multiline
                rows={3}
                variant="outlined"
                disabled={isLocked}
                placeholder="Describe your trip plan..."
              />
            </Grid>
          </Grid>
        </Paper>

        {/* ═══ Admin: Trip Settings (superpowers) ═══ */}
        {isAdmin && (
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2.5, md: 4 },
              mb: 4,
              borderRadius: 4,
              border: `2px solid ${alpha(theme.palette.secondary.main, 0.4)}`,
              background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.06)} 0%, ${alpha(theme.palette.primary.main, 0.04)} 100%)`,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.secondary.dark, display: 'flex', alignItems: 'center', gap: 1 }}>
                <WorkspacePremium /> Admin: Trip Settings
              </Typography>
              {published && (
                <Chip
                  label={`${published.visibility === 'public' ? 'Live' : published.visibility === 'draft' ? 'Draft' : 'Hidden'}`}
                  color={published.visibility === 'public' ? 'success' : 'default'}
                  size="small"
                  sx={{ fontWeight: 700 }}
                />
              )}
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Sell price"
                  type="number"
                  value={form.admin?.sell_price ?? ''}
                  onChange={(e) => updateAdmin('sell_price', e.target.value)}
                  fullWidth
                  disabled={isLocked}
                  InputProps={{
                    startAdornment: (
                      <Typography sx={{ mr: 0.5, fontWeight: 600, color: theme.palette.text.secondary }}>
                        {getCurrencySymbol(form.currency)}
                      </Typography>
                    ),
                  }}
                  helperText={`Cost ${formatCurrency(calculatedTotalNum, form.currency)} · Margin ${formatCurrency(marginNum, form.currency)}`}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth disabled={isLocked}>
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={form.admin?.category ?? 'international'}
                    label="Category"
                    onChange={(e) => updateAdmin('category', e.target.value)}
                  >
                    <MenuItem value="domestic">Domestic</MenuItem>
                    <MenuItem value="international">International</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth disabled={isLocked}>
                  <InputLabel>Difficulty</InputLabel>
                  <Select
                    value={form.admin?.difficulty ?? 'moderate'}
                    label="Difficulty"
                    onChange={(e) => updateAdmin('difficulty', e.target.value)}
                  >
                    <MenuItem value="easy">Easy</MenuItem>
                    <MenuItem value="moderate">Moderate</MenuItem>
                    <MenuItem value="challenging">Challenging</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} md={4}>
                <TextField
                  label="Max group size"
                  type="number"
                  value={form.admin?.max_group_size ?? ''}
                  onChange={(e) => updateAdmin('max_group_size', e.target.value)}
                  fullWidth
                  disabled={isLocked}
                  inputProps={{ min: 1 }}
                />
              </Grid>
              <Grid item xs={6} md={4} sx={{ display: 'flex', alignItems: 'center' }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(form.admin?.featured)}
                      onChange={(e) => updateAdmin('featured', e.target.checked)}
                      disabled={isLocked}
                    />
                  }
                  label="Featured"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Internal notes (admin only)"
                  value={form.admin?.internal_notes ?? ''}
                  onChange={(e) => updateAdmin('internal_notes', e.target.value)}
                  fullWidth
                  multiline
                  rows={2}
                  disabled={isLocked}
                  placeholder="Notes for the team. Never shown to customers."
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
              <ButtonPill
                variant="contained"
                color="secondary"
                startIcon={publishing ? <CircularProgress size={18} color="inherit" /> : <Public />}
                onClick={() => handlePublish('public')}
                disabled={publishing}
              >
                {published?.visibility === 'public' ? 'Update & Publish' : 'Publish as Trip'}
              </ButtonPill>
              <ButtonPill variant="outlined" onClick={() => handlePublish('draft')} disabled={publishing}>
                Save as Draft
              </ButtonPill>
              {published && published.visibility !== 'hidden' && (
                <ButtonPill variant="outlined" color="error" onClick={() => handlePublish('hidden')} disabled={publishing}>
                  Hide from Public
                </ButtonPill>
              )}
              {published?.id && (
                <ButtonPill variant="text" startIcon={<LinkIcon />} onClick={() => navigate(`/trips/${published.id}`)}>
                  View live trip
                </ButtonPill>
              )}
            </Box>
          </Paper>
        )}

        {/* ═══ Budget & Cost Breakdown ═══ */}
        <Paper
          elevation={0}
          sx={{ p: { xs: 2.5, md: 4 }, mb: 4, borderRadius: 4, border: `2px solid ${theme.palette.grey[200]}` }}
        >
          <Typography
            variant="h5"
            sx={{ fontWeight: 700, mb: 3, color: theme.palette.primary.main }}
          >
            Budget Summary
          </Typography>

          <Grid container spacing={3}>
            {/* Budget vs Actual */}
            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                    Budget
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {formatCurrency(form.total_budget, form.currency)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                    Calculated Total
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 700,
                      color:
                        budgetPercentage > 100
                          ? theme.palette.error.main
                          : theme.palette.success.main,
                    }}
                  >
                    {formatCurrency(calculatedTotal, form.currency)}
                  </Typography>
                </Box>
                {parseFloat(form.total_budget) > 0 && (
                  <>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min(budgetPercentage, 100)}
                      sx={{
                        height: 10,
                        borderRadius: 5,
                        bgcolor: alpha(theme.palette.grey[400], 0.2),
                        '& .MuiLinearProgress-bar': {
                          borderRadius: 5,
                          bgcolor:
                            budgetPercentage > 100
                              ? theme.palette.error.main
                              : budgetPercentage > 80
                                ? theme.palette.warning.main
                                : theme.palette.success.main,
                        },
                      }}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">
                        {budgetPercentage.toFixed(0)}% of budget used
                      </Typography>
                      {budgetPercentage > 100 && (
                        <Typography
                          variant="caption"
                          sx={{ color: theme.palette.error.main, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 0.3 }}
                        >
                          <Warning sx={{ fontSize: 14 }} />
                          Over budget by{' '}
                          {formatCurrency(
                            calculatedTotal - parseFloat(form.total_budget),
                            form.currency
                          )}
                        </Typography>
                      )}
                    </Box>
                  </>
                )}
              </Box>
            </Grid>

            {/* Cost breakdown by type */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
                Cost Breakdown
              </Typography>
              {Object.keys(costBreakdown).length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Add items with costs to see breakdown
                </Typography>
              ) : (
                <Stack spacing={1}>
                  {Object.entries(costBreakdown)
                    .sort(([, a], [, b]) => b - a)
                    .map(([type, cost]) => {
                      const Icon = ITEM_TYPE_ICONS[type] || MoreHoriz;
                      const pct = calculatedTotal > 0 ? (cost / calculatedTotal) * 100 : 0;
                      return (
                        <Box key={type}>
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              mb: 0.3,
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Icon
                                sx={{ fontSize: 16, color: ITEM_TYPE_COLORS[type] || theme.palette.text.secondary }}
                              />
                              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                {ITEM_TYPE_LABELS[type] || type}
                              </Typography>
                            </Box>
                            <Typography variant="caption" sx={{ fontWeight: 700 }}>
                              {formatCurrency(cost, form.currency)} ({pct.toFixed(0)}%)
                            </Typography>
                          </Box>
                          <LinearProgress
                            variant="determinate"
                            value={pct}
                            sx={{
                              height: 6,
                              borderRadius: 3,
                              bgcolor: alpha(theme.palette.grey[400], 0.15),
                              '& .MuiLinearProgress-bar': {
                                borderRadius: 3,
                                bgcolor: ITEM_TYPE_COLORS[type] || theme.palette.primary.main,
                              },
                            }}
                          />
                        </Box>
                      );
                    })}
                </Stack>
              )}
            </Grid>
          </Grid>
        </Paper>

        {/* ═══ Day-by-Day Itinerary ═══ */}
        <Paper
          elevation={0}
          sx={{ p: { xs: 2.5, md: 4 }, mb: 4, borderRadius: 4, border: `2px solid ${theme.palette.grey[200]}` }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 3,
              flexWrap: 'wrap',
              gap: 1,
            }}
          >
            <Typography
              variant="h5"
              sx={{ fontWeight: 700, color: theme.palette.primary.main }}
            >
              Day-by-Day Itinerary
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {form.plan_days.length > 0 && (
                <ButtonPill variant="text" size="small" onClick={toggleAllDays}>
                  {expandAll ? 'Collapse All' : 'Expand All'}
                </ButtonPill>
              )}
              {isDraft && (
                <ButtonPill variant="outlined" startIcon={<Add />} onClick={addDay}>
                  Add Day
                </ButtonPill>
              )}
            </Box>
          </Box>

          <Stack spacing={2}>
            {form.plan_days.map((day, dayIndex) => {
              const dayId = day.id ?? `day-${dayIndex}`;
              const isExpanded = expandedDays[dayId] !== false;
              const dayItemsCount = (day.plan_items || []).length;
              const dayCost = (day.plan_items || []).reduce(
                (s, i) => s + (parseFloat(i.cost) || 0),
                0
              );

              return (
                <Card
                  key={dayId}
                  sx={{
                    overflow: 'hidden',
                    border: `2px solid ${theme.palette.grey[200]}`,
                    borderRadius: 3,
                    transition: 'border-color 0.2s ease',
                    '&:hover': { borderColor: alpha(theme.palette.primary.main, 0.3) },
                  }}
                >
                  {/* Day header */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      p: 2,
                      bgcolor: alpha(theme.palette.primary.main, 0.06),
                      cursor: 'pointer',
                    }}
                    onClick={() => toggleDay(dayId)}
                  >
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 0 }}
                    >
                      {/* Day reorder buttons */}
                      {isDraft && (
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                          <IconButton
                            size="small"
                            disabled={dayIndex === 0}
                            onClick={(e) => {
                              e.stopPropagation();
                              moveDayUp(dayIndex);
                            }}
                            sx={{ p: 0 }}
                          >
                            <KeyboardArrowUp fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            disabled={dayIndex === form.plan_days.length - 1}
                            onClick={(e) => {
                              e.stopPropagation();
                              moveDayDown(dayIndex);
                            }}
                            sx={{ p: 0 }}
                          >
                            <KeyboardArrowDown fontSize="small" />
                          </IconButton>
                        </Box>
                      )}

                      <Chip
                        label={`Day ${day.day || dayIndex + 1}`}
                        color="primary"
                        size="small"
                        sx={{ fontWeight: 700 }}
                      />
                      {isDraft ? (
                        <TextField
                          value={day.title}
                          onChange={(e) => updateDay(dayIndex, 'title', e.target.value)}
                          placeholder="Day title (e.g. Arrival & Safari Drive)"
                          variant="standard"
                          size="small"
                          onClick={(e) => e.stopPropagation()}
                          sx={{ flex: 1, minWidth: 120 }}
                          InputProps={{ disableUnderline: !day.title, sx: { fontWeight: 600 } }}
                        />
                      ) : (
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }} noWrap>
                          {day.title || `Day ${day.day || dayIndex + 1}`}
                        </Typography>
                      )}
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                      <Chip
                        label={`${dayItemsCount} item${dayItemsCount !== 1 ? 's' : ''}`}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '0.7rem' }}
                      />
                      {dayCost > 0 && (
                        <Chip
                          label={formatCurrency(dayCost, form.currency)}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: '0.7rem', color: theme.palette.primary.main, borderColor: theme.palette.primary.main }}
                        />
                      )}
                      {isDraft && (
                        <Tooltip title="Remove day">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              removeDay(dayIndex);
                            }}
                            color="error"
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {isExpanded ? <ExpandLess /> : <ExpandMore />}
                    </Box>
                  </Box>

                  {/* Day content */}
                  <Collapse in={isExpanded}>
                    <CardContent sx={{ pt: 2 }}>
                      {/* Day date & notes */}
                      <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid item xs={12} sm={4}>
                          <TextField
                            label="Date"
                            type="date"
                            value={day.date}
                            onChange={(e) => updateDay(dayIndex, 'date', e.target.value)}
                            fullWidth
                            size="small"
                            variant="outlined"
                            disabled={isLocked}
                            InputLabelProps={{ shrink: true }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={8}>
                          <TextField
                            label="Notes"
                            value={day.notes}
                            onChange={(e) => updateDay(dayIndex, 'notes', e.target.value)}
                            fullWidth
                            size="small"
                            variant="outlined"
                            disabled={isLocked}
                            placeholder="Optional notes for this day..."
                          />
                        </Grid>
                      </Grid>

                      <Divider sx={{ mb: 2 }} />

                      {/* Items */}
                      <Typography
                        variant="subtitle2"
                        sx={{ mb: 1.5, fontWeight: 700, color: theme.palette.text.secondary }}
                      >
                        Items
                      </Typography>

                      <Stack spacing={1.5}>
                        {(day.plan_items || []).map((item, itemIndex) => {
                          const ItemIcon = ITEM_TYPE_ICONS[item.item_type] || MoreHoriz;
                          const itemColor =
                            ITEM_TYPE_COLORS[item.item_type] || theme.palette.text.secondary;
                          const isEditing =
                            editingItem?.dayIndex === dayIndex &&
                            editingItem?.itemIndex === itemIndex;

                          return (
                            <Card
                              key={item.id ?? `item-${itemIndex}`}
                              variant="outlined"
                              sx={{
                                borderRadius: 2,
                                borderLeft: `4px solid ${itemColor}`,
                                transition: 'box-shadow 0.2s ease',
                                '&:hover': { boxShadow: theme.shadows[2] },
                              }}
                            >
                              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                                {isEditing ? (
                                  /* ─── Edit Mode ─── */
                                  <Box>
                                    <Grid container spacing={2}>
                                      <Grid item xs={12} sm={3}>
                                        <FormControl size="small" fullWidth>
                                          <InputLabel>Type</InputLabel>
                                          <Select
                                            value={item.item_type}
                                            onChange={(e) =>
                                              updateItem(
                                                dayIndex,
                                                itemIndex,
                                                'item_type',
                                                e.target.value
                                              )
                                            }
                                            label="Type"
                                          >
                                            {Object.entries(ITEM_TYPE_LABELS).map(([k, v]) => {
                                              const Ic = ITEM_TYPE_ICONS[k];
                                              return (
                                                <MenuItem key={k} value={k}>
                                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Ic sx={{ fontSize: 18, color: ITEM_TYPE_COLORS[k] }} />
                                                    {v}
                                                  </Box>
                                                </MenuItem>
                                              );
                                            })}
                                          </Select>
                                        </FormControl>
                                      </Grid>
                                      <Grid item xs={12} sm={5}>
                                        <TextField
                                          size="small"
                                          label="Name"
                                          value={item.name}
                                          onChange={(e) =>
                                            updateItem(dayIndex, itemIndex, 'name', e.target.value)
                                          }
                                          fullWidth
                                          required
                                          placeholder={`e.g. ${item.item_type === 'flight' ? 'NBO → MOM Flight' : item.item_type === 'hotel' ? 'Serena Hotel' : item.item_type === 'meal' ? 'Lunch at Lodge' : 'Game Drive'}`}
                                        />
                                      </Grid>
                                      <Grid item xs={6} sm={2}>
                                        <TextField
                                          size="small"
                                          label="Cost"
                                          type="number"
                                          value={item.cost}
                                          onChange={(e) =>
                                            updateItem(dayIndex, itemIndex, 'cost', e.target.value)
                                          }
                                          fullWidth
                                          InputProps={{
                                            startAdornment: (
                                              <Typography
                                                variant="caption"
                                                sx={{ mr: 0.5, color: theme.palette.text.secondary }}
                                              >
                                                {getCurrencySymbol(form.currency)}
                                              </Typography>
                                            ),
                                          }}
                                        />
                                      </Grid>
                                      <Grid
                                        item
                                        xs={6}
                                        sm={2}
                                        sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                                      >
                                        <Tooltip title="Done editing">
                                          <IconButton
                                            size="small"
                                            onClick={() => setEditingItem(null)}
                                            color="primary"
                                          >
                                            <Check fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Remove item">
                                          <IconButton
                                            size="small"
                                            color="error"
                                            onClick={() =>
                                              removeItem(dayIndex, itemIndex)
                                            }
                                          >
                                            <Delete fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                      </Grid>

                                      {/* Location */}
                                      <Grid item xs={12} sm={4}>
                                        <TextField
                                          size="small"
                                          label="Location"
                                          value={item.location}
                                          onChange={(e) =>
                                            updateItem(
                                              dayIndex,
                                              itemIndex,
                                              'location',
                                              e.target.value
                                            )
                                          }
                                          fullWidth
                                          placeholder="e.g. Masai Mara"
                                          InputProps={{
                                            startAdornment: (
                                              <LocationOn
                                                sx={{
                                                  fontSize: 18,
                                                  mr: 0.5,
                                                  color: theme.palette.text.secondary,
                                                }}
                                              />
                                            ),
                                          }}
                                        />
                                      </Grid>

                                      {/* Time fields */}
                                      <Grid item xs={6} sm={4}>
                                        <TextField
                                          size="small"
                                          label="Start Time"
                                          type="time"
                                          value={item.start_time}
                                          onChange={(e) =>
                                            updateItem(
                                              dayIndex,
                                              itemIndex,
                                              'start_time',
                                              e.target.value
                                            )
                                          }
                                          fullWidth
                                          InputLabelProps={{ shrink: true }}
                                          InputProps={{
                                            startAdornment: (
                                              <AccessTime
                                                sx={{
                                                  fontSize: 18,
                                                  mr: 0.5,
                                                  color: theme.palette.text.secondary,
                                                }}
                                              />
                                            ),
                                          }}
                                        />
                                      </Grid>
                                      <Grid item xs={6} sm={4}>
                                        <TextField
                                          size="small"
                                          label="End Time"
                                          type="time"
                                          value={item.end_time}
                                          onChange={(e) =>
                                            updateItem(
                                              dayIndex,
                                              itemIndex,
                                              'end_time',
                                              e.target.value
                                            )
                                          }
                                          fullWidth
                                          InputLabelProps={{ shrink: true }}
                                        />
                                      </Grid>

                                      {/* Description */}
                                      <Grid item xs={12}>
                                        <TextField
                                          size="small"
                                          label="Description"
                                          value={item.description}
                                          onChange={(e) =>
                                            updateItem(
                                              dayIndex,
                                              itemIndex,
                                              'description',
                                              e.target.value
                                            )
                                          }
                                          fullWidth
                                          multiline
                                          rows={2}
                                          placeholder="Additional details..."
                                        />
                                      </Grid>
                                    </Grid>
                                  </Box>
                                ) : (
                                  /* ─── View Mode ─── */
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'flex-start',
                                      justifyContent: 'space-between',
                                      gap: 1,
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        gap: 1.5,
                                        flex: 1,
                                        minWidth: 0,
                                      }}
                                    >
                                      <Tooltip title={ITEM_TYPE_LABELS[item.item_type] || item.item_type}>
                                        <Box
                                          sx={{
                                            width: 32,
                                            height: 32,
                                            borderRadius: 1,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            bgcolor: alpha(itemColor, 0.1),
                                            flexShrink: 0,
                                          }}
                                        >
                                          <ItemIcon sx={{ color: itemColor, fontSize: 18 }} />
                                        </Box>
                                      </Tooltip>
                                      <Box sx={{ minWidth: 0, flex: 1 }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }} noWrap>
                                          {item.name || 'Untitled'}
                                        </Typography>
                                        {item.description && (
                                          <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{
                                              overflow: 'hidden',
                                              textOverflow: 'ellipsis',
                                              display: '-webkit-box',
                                              WebkitLineClamp: 2,
                                              WebkitBoxOrient: 'vertical',
                                            }}
                                          >
                                            {item.description}
                                          </Typography>
                                        )}
                                        <Box
                                          sx={{
                                            display: 'flex',
                                            flexWrap: 'wrap',
                                            gap: 1,
                                            mt: 0.5,
                                          }}
                                        >
                                          {item.location && (
                                            <Typography
                                              variant="caption"
                                              sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 0.3,
                                                color: theme.palette.text.secondary,
                                              }}
                                            >
                                              <LocationOn sx={{ fontSize: 13 }} />
                                              {item.location}
                                            </Typography>
                                          )}
                                          {(item.start_time || item.end_time) && (
                                            <Typography
                                              variant="caption"
                                              sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 0.3,
                                                color: theme.palette.text.secondary,
                                              }}
                                            >
                                              <AccessTime sx={{ fontSize: 13 }} />
                                              {item.start_time || '?'}
                                              {item.end_time ? ` \u2013 ${item.end_time}` : ''}
                                            </Typography>
                                          )}
                                          <Typography
                                            variant="caption"
                                            sx={{
                                              color: theme.palette.primary.main,
                                              fontWeight: 700,
                                            }}
                                          >
                                            {formatCurrency(item.cost, form.currency)}
                                          </Typography>
                                        </Box>
                                      </Box>
                                    </Box>

                                    {/* Item actions */}
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0,
                                        flexShrink: 0,
                                      }}
                                    >
                                      {isDraft && (
                                        <>
                                          <Tooltip title="Move up">
                                            <span>
                                              <IconButton
                                                size="small"
                                                disabled={itemIndex === 0}
                                                onClick={() => moveItemUp(dayIndex, itemIndex)}
                                              >
                                                <KeyboardArrowUp fontSize="small" />
                                              </IconButton>
                                            </span>
                                          </Tooltip>
                                          <Tooltip title="Move down">
                                            <span>
                                              <IconButton
                                                size="small"
                                                disabled={
                                                  itemIndex ===
                                                  (day.plan_items || []).length - 1
                                                }
                                                onClick={() =>
                                                  moveItemDown(dayIndex, itemIndex)
                                                }
                                              >
                                                <KeyboardArrowDown fontSize="small" />
                                              </IconButton>
                                            </span>
                                          </Tooltip>
                                          <Tooltip title="Edit">
                                            <IconButton
                                              size="small"
                                              onClick={() =>
                                                setEditingItem({ dayIndex, itemIndex })
                                              }
                                            >
                                              <Edit fontSize="small" />
                                            </IconButton>
                                          </Tooltip>
                                        </>
                                      )}
                                    </Box>
                                  </Box>
                                )}
                              </CardContent>
                            </Card>
                          );
                        })}
                      </Stack>

                      {/* Add item buttons */}
                      {isDraft && (
                        <Box
                          sx={{
                            mt: 2,
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 1,
                          }}
                        >
                          {Object.entries(ITEM_TYPE_LABELS).map(([type, label]) => {
                            const Icon = ITEM_TYPE_ICONS[type];
                            return (
                              <Tooltip key={type} title={`Add ${label}`}>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<Icon sx={{ fontSize: 16 }} />}
                                  onClick={() => addItem(dayIndex, type)}
                                  sx={{
                                    borderRadius: 9999,
                                    textTransform: 'none',
                                    fontSize: '0.75rem',
                                    borderColor: alpha(ITEM_TYPE_COLORS[type], 0.4),
                                    color: ITEM_TYPE_COLORS[type],
                                    '&:hover': {
                                      borderColor: ITEM_TYPE_COLORS[type],
                                      bgcolor: alpha(ITEM_TYPE_COLORS[type], 0.05),
                                    },
                                  }}
                                >
                                  {label}
                                </Button>
                              </Tooltip>
                            );
                          })}
                        </Box>
                      )}
                    </CardContent>
                  </Collapse>
                </Card>
              );
            })}
          </Stack>

          {/* Empty state */}
          {form.plan_days.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <CalendarMonth
                sx={{ fontSize: 64, color: theme.palette.text.disabled, mb: 2 }}
              />
              <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                No days yet. Add your first day to start building your itinerary.
              </Typography>
              {isDraft && (
                <ButtonPill variant="contained" startIcon={<Add />} onClick={addDay}>
                  Add Day
                </ButtonPill>
              )}
            </Box>
          )}
        </Paper>

        {/* ═══ Action Bar ═══ */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: 4,
            border: `2px solid ${theme.palette.grey[200]}`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {isLocked && (
              <ButtonPill
                variant="outlined"
                startIcon={<LockOpen />}
                onClick={() => {
                  setStatusAction('unlock');
                  setStatusDialogOpen(true);
                }}
              >
                Reopen for Editing
              </ButtonPill>
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <ButtonPill variant="outlined" onClick={() => navigate('/user/plans')}>
              {isDraft ? 'Close' : 'Back to Plans'}
            </ButtonPill>
            {isDraft && (
              <ButtonPill
                variant="contained"
                onClick={() => handleSave(false)}
                disabled={saving || !hasUnsavedChanges}
                startIcon={
                  saving ? <CircularProgress size={18} /> : <Save />
                }
              >
                {saving ? 'Saving...' : 'Save Plan'}
              </ButtonPill>
            )}
          </Box>
        </Paper>
      </Container>

      {/* ═══ Dialogs ═══ */}

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          variant="filled"
          sx={{ borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirm.open}
        onClose={() => setDeleteConfirm({ open: false, type: null, target: null })}
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          {deleteConfirm.type === 'plan'
            ? 'Delete this plan?'
            : deleteConfirm.type === 'day'
              ? 'Remove this day?'
              : 'Remove this item?'}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {deleteConfirm.type === 'plan'
              ? 'This will permanently delete the plan and all its days and items. This cannot be undone.'
              : deleteConfirm.type === 'day'
                ? 'This will remove the day and all its items. You can undo by not saving.'
                : 'This item will be removed. You can undo by not saving.'}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setDeleteConfirm({ open: false, type: null, target: null })}
            sx={{ borderRadius: 9999 }}
          >
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            sx={{ borderRadius: 9999 }}
            onClick={() => {
              if (deleteConfirm.type === 'plan') handleDelete();
              else if (deleteConfirm.type === 'day') confirmRemoveDay();
              else if (deleteConfirm.type === 'item') confirmRemoveItem();
            }}
          >
            {deleteConfirm.type === 'plan' ? 'Delete Plan' : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Status Change Dialog */}
      <Dialog
        open={statusDialogOpen}
        onClose={() => setStatusDialogOpen(false)}
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          {statusAction === 'lock' ? 'Mark as ready?' : 'Reopen this plan?'}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {statusAction === 'lock'
              ? 'This saves the current version and marks it ready. You can reopen it to edit anytime.'
              : 'This reopens the plan so you can make changes. You can send or share it again anytime.'}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setStatusDialogOpen(false)} sx={{ borderRadius: 9999 }}>
            Cancel
          </Button>
          <ButtonPill
            variant="contained"
            onClick={handleStatusChange}
            disabled={saving}
            startIcon={
              saving ? (
                <CircularProgress size={18} />
              ) : statusAction === 'lock' ? (
                <Lock />
              ) : (
                <LockOpen />
              )
            }
          >
            {statusAction === 'lock' ? 'Mark as Ready' : 'Reopen'}
          </ButtonPill>
        </DialogActions>
      </Dialog>

      {/* Request This Trip Dialog */}
      <Dialog
        open={requestDialogOpen}
        onClose={() => !requesting && setRequestDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        {requestDone ? (
          <DialogContent sx={{ textAlign: 'center', py: 5 }}>
            <CheckCircle sx={{ fontSize: 64, color: theme.palette.success.main, mb: 2 }} />
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
              Request received
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3, maxWidth: 440, mx: 'auto' }}>
              Thanks! Our team has your itinerary and will reach out shortly with a custom quote.
              No payment is due yet, and nothing is booked until you confirm.
            </Typography>
            <ButtonPill variant="contained" onClick={() => setRequestDialogOpen(false)}>
              Done
            </ButtonPill>
          </DialogContent>
        ) : (
          <>
            <DialogTitle sx={{ fontWeight: 700 }}>Request This Trip</DialogTitle>
            <DialogContent>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                This sends your itinerary to our team for a free, custom quote. No payment now, and
                nothing is booked until you confirm.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Add a few details so we can tailor it to you.
              </Typography>
              <Grid container spacing={2.5}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Number of travelers"
                    type="number"
                    value={requestForm.num_travelers}
                    onChange={(e) => setRequestForm((f) => ({ ...f, num_travelers: e.target.value }))}
                    fullWidth
                    inputProps={{ min: 1 }}
                    InputProps={{
                      startAdornment: (
                        <Groups sx={{ fontSize: 20, mr: 0.75, color: theme.palette.text.secondary }} />
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Preferred date"
                    type="date"
                    value={requestForm.preferred_date}
                    onChange={(e) => setRequestForm((f) => ({ ...f, preferred_date: e.target.value }))}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Message (optional)"
                    value={requestForm.message}
                    onChange={(e) => setRequestForm((f) => ({ ...f, message: e.target.value }))}
                    fullWidth
                    multiline
                    rows={3}
                    placeholder="Anything we should know? Dietary needs, special occasions, flexible dates..."
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5 }}>
              <Button onClick={() => setRequestDialogOpen(false)} disabled={requesting} sx={{ borderRadius: 9999 }}>
                Cancel
              </Button>
              <ButtonPill
                variant="contained"
                onClick={handleRequestSubmit}
                disabled={requesting}
                startIcon={requesting ? <CircularProgress size={18} /> : <Send />}
              >
                {requesting ? 'Sending...' : 'Send Request'}
              </ButtonPill>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Book approved plan */}
      <BookPlanDialog
        open={bookDialogOpen}
        onClose={() => setBookDialogOpen(false)}
        planId={plan?.id}
        defaultTravelers={myOpenRequest?.num_travelers}
        defaultTravelDate={form.start_date}
      />

      {/* Share Plan Dialog */}
      <Dialog
        open={shareDialogOpen}
        onClose={() => setShareDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Share Plan</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
            {isShared
              ? 'Anyone with the link below can view a read-only copy of this itinerary, including its estimated costs. Turn sharing off to disable the link.'
              : 'Turn on sharing to create a public, read-only link you can send to anyone. They can see the itinerary and its estimated costs, but no one can edit it.'}
          </Typography>

          {isShared && shareUrl && (
            <Box
              sx={{
                p: 1.5,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.primary.main, 0.05),
                border: `1px solid ${theme.palette.grey[200]}`,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                flexWrap: 'wrap',
              }}
            >
              <Public sx={{ fontSize: 18, color: theme.palette.success.main }} />
              <Typography variant="body2" sx={{ flex: 1, minWidth: 160, wordBreak: 'break-all' }}>
                {shareUrl}
              </Typography>
              <ButtonPill
                variant="text"
                size="small"
                startIcon={<ContentCopy sx={{ fontSize: 16 }} />}
                onClick={handleCopyLink}
              >
                {linkCopied ? 'Copied' : 'Copy'}
              </ButtonPill>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setShareDialogOpen(false)} sx={{ borderRadius: 9999 }}>
            Close
          </Button>
          <ButtonPill
            variant={isShared ? 'outlined' : 'contained'}
            color={isShared ? 'error' : 'primary'}
            onClick={handleShareToggle}
            disabled={sharing}
            startIcon={sharing ? <CircularProgress size={18} /> : <Share />}
          >
            {sharing ? 'Updating...' : isShared ? 'Disable Sharing' : 'Enable Share Link'}
          </ButtonPill>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TripBuilder;
